import type { ApiClient } from "@/api/client";
import { API_VERSION_PREFIX, newIdempotencyKey } from "@/api/client";
import type {
  InternalNoteDto,
  PaginationMeta,
  RequestMeta,
  SupportConversationAction,
  SupportConversationDto,
  SupportTicketAction,
  SupportTicketDto,
  SupportTicketLinkDto,
  TicketsListParams,
} from "./types";

const BASE = `${API_VERSION_PREFIX}/hq/support`;

function toQuery(params: TicketsListParams | Record<string, string | number | undefined> = {}): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "") continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export function createSupportApi(client: ApiClient) {
  return {
    listTickets(params: TicketsListParams = {}) {
      return client.get<{
        tickets: SupportTicketDto[];
        pagination: PaginationMeta;
        meta: RequestMeta;
      }>(`${BASE}/tickets${toQuery(params)}`);
    },

    getTicket(id: string) {
      return client.get<{ ticket: SupportTicketDto; meta: RequestMeta }>(
        `${BASE}/tickets/${id}`,
      );
    },

    createTicket(body: Record<string, unknown>) {
      return client.post<{ ticket: SupportTicketDto; meta: RequestMeta }>(
        `${BASE}/tickets`,
        body,
        newIdempotencyKey(),
      );
    },

    applyTicketAction(
      id: string,
      action: SupportTicketAction,
      body: Record<string, unknown>,
    ) {
      return client.post(
        `${BASE}/tickets/${id}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    listLinks(ticketId: string, params: Record<string, string | number | undefined> = {}) {
      return client.get<{
        links: SupportTicketLinkDto[];
        pagination: PaginationMeta;
        meta: RequestMeta;
      }>(`${BASE}/tickets/${ticketId}/links${toQuery(params)}`);
    },

    createLink(ticketId: string, body: Record<string, unknown>) {
      return client.post(
        `${BASE}/tickets/${ticketId}/links`,
        body,
        newIdempotencyKey(),
      );
    },

    deleteLink(linkId: string) {
      return client.request<{
        link_id: string;
        detached: boolean;
        meta: RequestMeta;
      }>(`${BASE}/links/${linkId}`, {
        method: "DELETE",
        idempotencyKey: newIdempotencyKey(),
      });
    },

    listConversations(
      ticketId: string,
      params: Record<string, string | number | undefined> = {},
    ) {
      return client.get<{
        conversations: SupportConversationDto[];
        pagination: PaginationMeta;
        meta: RequestMeta;
      }>(`${BASE}/tickets/${ticketId}/conversations${toQuery(params)}`);
    },

    createConversation(ticketId: string, body: Record<string, unknown>) {
      return client.post(
        `${BASE}/tickets/${ticketId}/conversations`,
        body,
        newIdempotencyKey(),
      );
    },

    applyConversationAction(
      conversationId: string,
      action: SupportConversationAction,
      body: Record<string, unknown>,
    ) {
      return client.post(
        `${BASE}/conversations/${conversationId}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    listNotes(
      ticketId: string,
      params: Record<string, string | number | undefined> = {},
    ) {
      return client.get<{
        internal_notes: InternalNoteDto[];
        pagination: PaginationMeta;
        meta: RequestMeta;
      }>(`${BASE}/tickets/${ticketId}/internal-notes${toQuery(params)}`);
    },

    createNote(ticketId: string, body: Record<string, unknown>) {
      return client.post(
        `${BASE}/tickets/${ticketId}/internal-notes`,
        body,
        newIdempotencyKey(),
      );
    },
  };
}

export type SupportApi = ReturnType<typeof createSupportApi>;
